import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button from '@/lib/components/Button';
import FormField from '@/lib/components/FormField';
import { getClientCapabilities } from '@/lib/utils/browserUtils';
// import ExclamationMarkIcon from '@/lib/assets/mark.svg';
import styles_psp from '@/lib/components/PspComposer/PspComposer.module.scss';
import styles from '@/lib/components/PaymentPlan/PaymentPlan.module.scss';
import InstalmentRadioGroup from './InstalmentRadioGroup';
import Loader from '@/lib/components/Loader';


export const PaymentPlanForm = ({
    paymentRequestId,
    paymentData,
    lyriaUrl,
    onPaymentResult,
    config,
    lang,
}) => {
    const { t } = useTranslation();
    const [frequencyOptions, setFrequencyOptions] = React.useState();
    const [configSelected, setConfigSelected] = React.useState(config.available_configs.find((elem) => elem.id == config?.default_config_id));
    const [selectedInstalment, setSelectedInstalment] = React.useState(configSelected?.default_instalment);
    const frequencySelected = configSelected.frequency.id;
    const [isLoading, setIsLoading] = React.useState(false);
    const [isWarningRefundChecked, setIsWarningRefundChecked] = React.useState(false);

    React.useEffect(() => {
        axios({
            method: 'GET',
            url: `${lyriaUrl}/instalment_plan/frequency/`,
        })
            .then((frequencies) => {
                const filteredFrequencies = frequencies.data.filter(item =>
                    config?.available_configs?.some(config => config.frequency.id === item.id),
                );
                setFrequencyOptions(filteredFrequencies.map((item) => ({
                    value: item.id,
                    label: item.name,
                })));
            });

    }, [config?.available_configs, lyriaUrl]);

    const setConfigFromFrequency = React.useCallback((_, frequencyId) => {
        const configToSet = config.available_configs.find((elem) => elem.frequency.id == frequencyId);
        setConfigSelected(configToSet);
    }, [config.available_configs]);

    const instalmentsOptions = React.useMemo(() => {
        const allowed_instalments = configSelected.allowed_instalments;

        return allowed_instalments.map((instalmentNumber) => {
            const amount = ((paymentData?.amount / 100) / instalmentNumber).toFixed(2);
            return {
                value: instalmentNumber,
                label: t('payMultipleTime', { count: instalmentNumber }),
                amount: amount,
            };
        });
    }, [configSelected.allowed_instalments, paymentData?.amount, t]);

    const submitPaymentPlan = React.useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const response = await fetch(
            `${lyriaUrl}/paymentplan/process`,
            {
                method: 'POST',
                body: JSON.stringify({
                    psp: config.id,
                    payment_request_id: paymentRequestId,
                    data: {
                        email: paymentData.email,
                        first_name: paymentData.first_name,
                        last_name: paymentData.last_name,
                        instalments: selectedInstalment,
                        frequency: Number(e.target.frequency.value),
                        timezone: 'GMT+0',
                    },
                    client_capabilities: getClientCapabilities(),
                }),
                headers: new Headers({ 'content-type': 'application/json' }),
            },
        );

        const data = await response.json();
        if (response.status == 400 && data?.data?.action?.next_payment_uuid) {
            const url = new URL(`${window.location.origin}/${data?.data?.action?.next_payment_uuid}`);
            if (lang) {
                url.searchParams.set('lng', lang);
            }
            window.top.location.href = url.href;
        }
        response.status === 404 && onPaymentResult({ success: false, data: { callback_url: paymentData?.failure_callback_url } });
    }, [
        config.id,
        lang,
        lyriaUrl,
        onPaymentResult,
        paymentData?.email,
        paymentData?.failure_callback_url,
        paymentData?.first_name,
        paymentData?.last_name,
        paymentRequestId,
        selectedInstalment,
    ]);

    return (
        <div id="wz-lyriapay__paymentplan-form" className={styles_psp.common_psp}>
            {isLoading ? <Loader /> :
                <>
                    <p className={styles.header}>{t('selectInstalment')}</p>
                    <form onSubmit={submitPaymentPlan}>
                        <div className={styles_psp.common_psp_form}>
                            <div className={styles_psp.common_field_group}>
                                <div>
                                    <FormField
                                        type='select'
                                        name="frequency"
                                        value={frequencySelected}
                                        onChange={setConfigFromFrequency}
                                        label={t('frequency')}
                                        options={frequencyOptions}
                                        disabled={frequencyOptions?.length < 2}
                                    />
                                </div>
                                <div className={styles.instalments}>
                                    {t('instalmentNumber')}
                                    <InstalmentRadioGroup
                                        instalmentsOptions={instalmentsOptions}
                                        setSelectedInstalment={setSelectedInstalment}
                                        selectedInstalment={selectedInstalment}
                                        paymentData={paymentData}
                                    />
                                    {/* <div className={styles.additional_fees}>
                                <ExclamationMarkIcon className={styles.mark_icon} />
                                {t('paymentPlanFees')}
                            </div> */}
                                </div>
                            </div>
                        </div>
                        {!config.allowed_refund &&
                            <label className={styles.warning_refundable}>
                                <input
                                    type="checkbox"
                                    name="acceptPartialPaymentPolicy"
                                    checked={isWarningRefundChecked}
                                    onChange={() => setIsWarningRefundChecked(!isWarningRefundChecked)}
                                    required
                                />
                                <p>
                                    {t('warningPlanRefundPolicy')}
                                </p>
                            </label>
                        }
                        <div className={styles_psp.common_psp_submit}>
                            <Button
                                label={t('pay')}
                                type='submit'
                                disabled={!frequencyOptions?.length || (!isWarningRefundChecked && !config.allowed_refund)}
                            />
                        </div>
                    </form>
                </>
            }
        </div>
    );
};

PaymentPlanForm.propTypes = {
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string,
    /**
     * Object containing the attendee's personal information (first name, last name, phone number, ...).
     */
    paymentData: PropTypes.object,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func,
    /**
     * The specific configuration related to this payment method.
     */
    config: PropTypes.object,
};
